import { CoinGeckoAPI } from './api';
const CACHE_KEY_PREFIX = 'token_price_history_';
const MA_EMA_CACHE_KEY = 'ma_ema_200_cache';
const CACHE_DURATION = 6 * 60 * 60 * 1000; // 6 hours (reduced for fresher Bitcoin price data)

interface TokenPriceHistory {
  [timestamp: number]: number; // Unix timestamp -> price
}

interface CacheData {
  prices: TokenPriceHistory;
  lastUpdated: number;
}

// Cache for calculated MA200/EMA200 values
interface MAEMACache {
  [tokenId: string]: {
    ma200: number | null;
    ema200: number | null;
    lastUpdated: number;
  };
}

/**
 * Token Price History Service
 * Manages historical token prices with localStorage caching
 */
export const TokenPriceHistoryService = {
  /**
   * Get cached price history for a token
   */
  getCache(tokenId: string, days: number): TokenPriceHistory | null {
    try {
      const cacheKey = `${CACHE_KEY_PREFIX}${tokenId}_${days}d`;
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const data: CacheData = JSON.parse(cached);

      // Check if cache is still valid
      if (Date.now() - data.lastUpdated > CACHE_DURATION) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return data.prices;
    } catch (error) {
      console.error(`Error reading price cache for ${tokenId}:`, error);
      return null;
    }
  },

  /**
   * Save price history to cache
   */
  saveCache(tokenId: string, days: number, prices: TokenPriceHistory): void {
    try {
      const cacheKey = `${CACHE_KEY_PREFIX}${tokenId}_${days}d`;
      const data: CacheData = {
        prices,
        lastUpdated: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (error) {
      console.error(`Error saving price cache for ${tokenId}:`, error);
    }
  },

  /**
   * Get price history for a token
   * @param tokenId - CoinGecko token ID (e.g., 'bitcoin', 'ethereum')
   * @param days - Number of days of history (1-365)
   * @returns Map of timestamp to price
   */
  async getTokenPriceHistory(tokenId: string, days: number): Promise<TokenPriceHistory> {
    // Check cache first
    const cached = this.getCache(tokenId, days);
    if (cached) {
      console.log(`✅ Price history for ${tokenId} (${days}d) found in cache`);
      return cached;
    }

    // Fetch from API using CoinGeckoAPI (handles proxy in production)
    try {
      console.log(`📡 Fetching price history for ${tokenId} (${days}d)...`);

      const data = await CoinGeckoAPI.getChartData(tokenId, days);

      if (data.prices && Array.isArray(data.prices)) {
        const prices: TokenPriceHistory = {};

        // Convert array format [[timestamp, price]] to object {timestamp: price}.
        // CoinGecko types the rows as `number[]` (variable length); we cast
        // to a fixed [number, number] tuple at the destructure since the
        // /charts endpoint always returns 2-tuples per the API contract.
        (data.prices as [number, number][]).forEach(([timestamp, price]) => {
          prices[timestamp] = price;
        });

        // Save to cache
        this.saveCache(tokenId, days, prices);

        console.log(`✅ Loaded ${Object.keys(prices).length} price points for ${tokenId}`);
        return prices;
      }

      console.warn(`⚠️ No price data for ${tokenId}`);
      return {};
    } catch (error) {
      console.error(`❌ Error fetching price history for ${tokenId}:`, error);
      return {};
    }
  },

  /**
   * Get price history for multiple tokens
   * @param tokenIds - Array of token IDs
   * @param days - Number of days of history
   * @returns Map of tokenId to price history
   */
  async getMultipleTokensPriceHistory(
    tokenIds: string[],
    days: number
  ): Promise<Record<string, TokenPriceHistory>> {
    const result: Record<string, TokenPriceHistory> = {};

    console.log(`📡 Fetching price history for ${tokenIds.length} tokens...`);

    for (const tokenId of tokenIds) {
      const history = await this.getTokenPriceHistory(tokenId, days);
      if (Object.keys(history).length > 0) {
        result[tokenId] = history;
      }

      // Small delay to avoid rate limiting (reduced from 500ms to 100ms for better UX)
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return result;
  },

  /**
   * Calculate Moving Average (MA) from price history
   * @param prices - Price history object {timestamp: price}
   * @param period - Number of periods (e.g., 200 for MA200)
   * @returns MA value or null if insufficient data
   */
  calculateMA(prices: TokenPriceHistory, period: number): number | null {
    const priceArray = Object.values(prices);

    if (priceArray.length < period) {
      console.warn(`⚠️ Insufficient data for MA${period}: ${priceArray.length} points available, ${period} required`);
      return null;
    }

    // Take the last 'period' prices
    const recentPrices = priceArray.slice(-period);

    // Calculate simple moving average
    const sum = recentPrices.reduce((acc, price) => acc + price, 0);
    const ma = sum / period;

    return ma;
  },

  /**
   * Get MA200 for a token
   * @param tokenId - CoinGecko token ID
   * @returns MA200 value or null
   */
  async getMA200(tokenId: string): Promise<number | null> {
    try {
      // Get 200 days of history (need at least 200 data points)
      const history = await this.getTokenPriceHistory(tokenId, 200);

      if (Object.keys(history).length === 0) {
        console.warn(`⚠️ No price history available for ${tokenId}`);
        return null;
      }

      return this.calculateMA(history, 200);
    } catch (error) {
      console.error(`❌ Error calculating MA200 for ${tokenId}:`, error);
      return null;
    }
  },

  /**
   * Get MA200 for multiple tokens
   * @param tokenIds - Array of token IDs
   * @returns Map of tokenId to MA200 value
   */
  async getMA200ForMultipleTokens(tokenIds: string[]): Promise<Record<string, number | null>> {
    const result: Record<string, number | null> = {};

    console.log(`📊 Calculating MA200 for ${tokenIds.length} tokens...`);

    for (const tokenId of tokenIds) {
      result[tokenId] = await this.getMA200(tokenId);

      // Small delay to avoid rate limiting (reduced from 500ms to 100ms for better UX)
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return result;
  },

  /**
   * Calculate Exponential Moving Average (EMA) from price history
   * EMA gives more weight to recent prices compared to SMA
   * @param prices - Price history object {timestamp: price}
   * @param period - Number of periods (e.g., 200 for EMA200)
   * @returns EMA value or null if insufficient data
   */
  calculateEMA(prices: TokenPriceHistory, period: number): number | null {
    // Sort by timestamp and get price array
    const sortedEntries = Object.entries(prices)
      .sort(([a], [b]) => Number(a) - Number(b));
    const priceArray = sortedEntries.map(([, price]) => price);

    if (priceArray.length < period) {
      console.warn(`⚠️ Insufficient data for EMA${period}: ${priceArray.length} points available, ${period} required`);
      return null;
    }

    // Calculate multiplier (smoothing factor)
    const k = 2 / (period + 1);

    // Start with SMA for the first 'period' values
    const initialSMA = priceArray.slice(0, period).reduce((sum, p) => sum + p, 0) / period;

    // Calculate EMA iteratively from the 'period' index onwards
    let ema = initialSMA;
    for (let i = period; i < priceArray.length; i++) {
      ema = (priceArray[i] * k) + (ema * (1 - k));
    }

    return ema;
  },

  /**
   * Get EMA200 for a token
   * @param tokenId - CoinGecko token ID
   * @returns EMA200 value or null
   */
  async getEMA200(tokenId: string): Promise<number | null> {
    try {
      // Get 200 days of history (need at least 200 data points)
      const history = await this.getTokenPriceHistory(tokenId, 200);

      if (Object.keys(history).length === 0) {
        console.warn(`⚠️ No price history available for ${tokenId}`);
        return null;
      }

      return this.calculateEMA(history, 200);
    } catch (error) {
      console.error(`❌ Error calculating EMA200 for ${tokenId}:`, error);
      return null;
    }
  },

  /**
   * Get EMA200 for multiple tokens
   * @param tokenIds - Array of token IDs
   * @returns Map of tokenId to EMA200 value
   */
  async getEMA200ForMultipleTokens(tokenIds: string[]): Promise<Record<string, number | null>> {
    const result: Record<string, number | null> = {};

    console.log(`📊 Calculating EMA200 for ${tokenIds.length} tokens...`);

    for (const tokenId of tokenIds) {
      result[tokenId] = await this.getEMA200(tokenId);

      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return result;
  },

  /**
   * Get both MA200 and EMA200 for multiple tokens (optimized - single price fetch)
   * Uses cached values as fallback when price history unavailable
   * @param tokenIds - Array of token IDs
   * @returns Map of tokenId to {ma200, ema200}
   */
  async getMA200AndEMA200ForMultipleTokens(tokenIds: string[]): Promise<Record<string, { ma200: number | null; ema200: number | null }>> {
    const result: Record<string, { ma200: number | null; ema200: number | null }> = {};
    const maEmaCache = this.loadMAEMACache();
    let cacheUpdated = false;

    console.log(`📊 Calculating MA200 & EMA200 for ${tokenIds.length} tokens...`);

    for (const tokenId of tokenIds) {
      try {
        const history = await this.getTokenPriceHistory(tokenId, 200);

        if (Object.keys(history).length === 0) {
          // Try to use cached MA/EMA as fallback
          const cached = this.getCachedMAEMA(tokenId);
          if (cached) {
            console.log(`💾 Using cached MA/EMA for ${tokenId}`);
            result[tokenId] = cached;
          } else {
            result[tokenId] = { ma200: null, ema200: null };
          }
        } else {
          const ma200 = this.calculateMA(history, 200);
          const ema200 = this.calculateEMA(history, 200);
          result[tokenId] = { ma200, ema200 };

          // Update cache with new calculated values
          maEmaCache[tokenId] = {
            ma200,
            ema200,
            lastUpdated: Date.now()
          };
          cacheUpdated = true;
        }
      } catch (error) {
        console.error(`❌ Error calculating MA/EMA for ${tokenId}:`, error);
        // Try fallback from cache
        const cached = this.getCachedMAEMA(tokenId);
        result[tokenId] = cached || { ma200: null, ema200: null };
      }

      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Save updated cache
    if (cacheUpdated) {
      this.saveMAEMACache(maEmaCache);
    }

    return result;
  },

  /**
   * Clear all caches
   */
  clearCache(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      localStorage.removeItem(MA_EMA_CACHE_KEY);
      console.log('🗑️ Token price history cache cleared');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  },

  /**
   * Load MA200/EMA200 cache from localStorage
   */
  loadMAEMACache(): MAEMACache {
    try {
      const cached = localStorage.getItem(MA_EMA_CACHE_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Error loading MA/EMA cache:', error);
    }
    return {};
  },

  /**
   * Save MA200/EMA200 cache to localStorage
   */
  saveMAEMACache(cache: MAEMACache): void {
    try {
      localStorage.setItem(MA_EMA_CACHE_KEY, JSON.stringify(cache));
      console.log(`💾 Saved MA/EMA cache for ${Object.keys(cache).length} token(s)`);
    } catch (error) {
      console.error('Error saving MA/EMA cache:', error);
    }
  },

  /**
   * Get cached MA200/EMA200 for a token (fallback when price history unavailable)
   */
  getCachedMAEMA(tokenId: string): { ma200: number | null; ema200: number | null } | null {
    const cache = this.loadMAEMACache();
    const entry = cache[tokenId];

    if (!entry) return null;

    // Check if cache is still valid (same duration as price history)
    if (Date.now() - entry.lastUpdated > CACHE_DURATION) {
      return null;
    }

    return { ma200: entry.ma200, ema200: entry.ema200 };
  },

  /**
   * Save calculated MA200/EMA200 to cache
   */
  saveCachedMAEMA(tokenId: string, ma200: number | null, ema200: number | null): void {
    const cache = this.loadMAEMACache();
    cache[tokenId] = {
      ma200,
      ema200,
      lastUpdated: Date.now()
    };
    this.saveMAEMACache(cache);
  }
};
